import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  badge?: string | number;
}

export interface TabsProps {
  items: TabItem[];
  defaultActiveTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline' | 'cards';
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  tabListClassName?: string;
  tabPanelClassName?: string;
  fullWidth?: boolean;
  centered?: boolean;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActiveTab,
  activeTab: externalActiveTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  orientation = 'horizontal',
  className,
  tabListClassName,
  tabPanelClassName,
  fullWidth = false,
  centered = false,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(
    externalActiveTab || defaultActiveTab || items[0]?.id
  );
  const [focusedIndex, setFocusedIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const isControlled = externalActiveTab !== undefined;
  const activeTab = isControlled ? externalActiveTab : internalActiveTab;

  const handleTabChange = (tabId: string) => {
    if (!isControlled) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    let newIndex = index;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (index + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = index === 0 ? items.length - 1 : index - 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = items.length - 1;
        break;
      default:
        return;
    }

 
    while (items[newIndex]?.disabled && newIndex !== index) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        newIndex = (newIndex + 1) % items.length;
      } else {
        newIndex = newIndex === 0 ? items.length - 1 : newIndex - 1;
      }
    }

    setFocusedIndex(newIndex);
    tabRefs.current[newIndex]?.focus();
  };

  const variantClasses = {
    default: {
      tabList: 'border-b border-gray-200 dark:border-gray-700',
      tab: 'border-b-2 border-transparent px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600',
      activeTab: 'border-blue-500 text-blue-600 dark:text-blue-400',
      disabledTab: 'text-gray-400 cursor-not-allowed hover:text-gray-400 hover:border-transparent dark:text-gray-600',
    },
    pills: {
      tabList: 'space-x-1',
      tab: 'px-3 py-2 text-sm font-medium rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700',
      activeTab: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      disabledTab: 'text-gray-400 cursor-not-allowed hover:text-gray-400 hover:bg-transparent dark:text-gray-600',
    },
    underline: {
      tabList: 'h-14 flex flex-row border border-gray-200 dark:border-gray-700 mb-4 rounded-xl gap-4',
      tab: 'border-b-2 border-transparent px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-0 focus:ring- focus:ring-offset-0 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600',
      activeTab: 'border-yellow-500 text-yellow-500 hover:text-yellow-600 hover:border-yellow-400 dark:text-blue-400',
      disabledTab: 'text-gray-400 cursor-not-allowed hover:text-gray-400 hover:border-transparent dark:text-gray-600',
    },
    cards: {
      tabList: 'space-x-1',
      tab: 'px-4 py-2 text-sm font-medium border border-gray-300 rounded-t-lg text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 dark:border-gray-600',
      activeTab: 'bg-white border-b-white text-blue-600 dark:bg-gray-800 dark:border-b-gray-800 dark:text-blue-400',
      disabledTab: 'text-gray-400 cursor-not-allowed hover:text-gray-400 hover:bg-transparent dark:text-gray-600',
    },
  };

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const currentVariant = variantClasses[variant];

  return (
    <div className={cn('w-full', className)}>
      <div
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          'flex',
          orientation === 'vertical'
            ? 'flex-col space-y-1'
            : 'flex-row flex-nowrap overflow-x-auto',
          fullWidth ? 'w-full' : '',
          centered ? 'justify-center' : '',
          currentVariant.tabList,
          tabListClassName
        )}
      >
        {items.map((item, index) => (
          <button
            key={item.id}
            ref={(el) => (tabRefs.current[index] = el)}
            role="tab"
            aria-selected={activeTab === item.id}
            aria-controls={`panel-${item.id}`}
            aria-disabled={item.disabled}
            tabIndex={activeTab === item.id ? 0 : -1}
            disabled={item.disabled}
            onClick={() => !item.disabled && handleTabChange(item.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'flex items-center space-x-2 transition-colors duration-200',
              fullWidth ? 'flex-1' : '',
              currentVariant.tab,
              activeTab === item.id ? currentVariant.activeTab : '',
              item.disabled ? currentVariant.disabledTab : '',
              sizeClasses[size]
            )}
          >
            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
            <span>{item.label}</span>
            {item.badge && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full dark:bg-gray-700 dark:text-gray-300">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`panel-${item.id}`}
          aria-labelledby={`tab-${item.id}`}
          className={cn(
            // 'mt-4 border border-gray-200 p-4 rounded-lg',
            activeTab === item.id ? 'block' : 'hidden',
            tabPanelClassName
          )}
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}; 